import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Card,
  Row,
  Col,
  Statistic,
  message,
  Popconfirm,
  Switch,
  InputNumber,
  Tooltip,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import { vatClientApi } from "../../api/vatApi";
import { IVatClient, VatClientType } from "../../types/vatClient.type";
import { formatNumber } from "../../utils/formatNumber";

const { Option } = Select;
const { TextArea } = Input;

const VatClientsPage: React.FC = () => {
  const [clients, setClients] = useState<IVatClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingClient, setEditingClient] = useState<IVatClient | null>(null);
  const [form] = Form.useForm();
  const [balance, setBalance] = useState<any>(null);
  const [searchText, setSearchText] = useState("");

  const fetchClients = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (searchText) {
        params.search = searchText;
      }
      const response = await vatClientApi.getAll(params);
      setClients(response.data);
    } catch (error) {
      message.error("Ошибка при загрузке клиентов НДС");
    } finally {
      setLoading(false);
    }
  };

  const fetchBalance = async () => {
    setBalanceLoading(true);
    try {
      const response = await vatClientApi.getBalance();
      setBalance(response.data);
    } catch (error) {
      message.error("Ошибка при загрузке баланса");
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchBalance();
  }, [searchText]);

  const handleCreate = () => {
    setEditingClient(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (client: IVatClient) => {
    setEditingClient(client);
    form.setFieldsValue({
      ...client,
      isActive: client.isActive,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    setDeleteLoading(id);
    try {
      await vatClientApi.delete(id);
      message.success("Клиент НДС успешно удален");
      fetchClients();
      fetchBalance();
    } catch (error) {
      message.error("Ошибка при удалении клиента НДС");
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitLoading(true);
    try {
      if (editingClient) {
        await vatClientApi.update(editingClient._id, values);
        message.success("Клиент НДС успешно обновлен");
      } else {
        await vatClientApi.create(values);
        message.success("Клиент НДС успешно создан");
      }
      setModalVisible(false);
      fetchClients();
      fetchBalance();
    } catch (error) {
      message.error("Ошибка при сохранении клиента НДС");
    } finally {
      setSubmitLoading(false);
    }
  };

  // Рассчитываем общий баланс
  const totalBalance = balance ? {
    USD: balance.clientDebt.USD - balance.ourDebt.USD,
    TJS: balance.clientDebt.TJS - balance.ourDebt.TJS
  } : { USD: 0, TJS: 0 };

  const columns = [
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      sorter: (a: IVatClient, b: IVatClient) => a.name.localeCompare(b.name),
    },
    {
      title: "ИНН",
      dataIndex: "tin",
      key: "tin",
    },
    {
      title: "Телефон",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Адрес",
      dataIndex: "address",
      key: "address",
      ellipsis: true,
    },
    {
      title: "Банк",
      dataIndex: ["bankDetails", "bankName"],
      key: "bankName",
      ellipsis: true,
    },
    {
      title: "Счет",
      dataIndex: ["bankDetails", "accountNumber"],
      key: "accountNumber",
      ellipsis: true,
    },
    {
      title: "МФО",
      dataIndex: ["bankDetails", "mfo"],
      key: "mfo",
      render: (value: string) => (
        <Tooltip title="Межфилиальный оборот - код банка">
          {value}
        </Tooltip>
      ),
    },
    {
      title: "Тип",
      dataIndex: "clientType",
      key: "clientType",
      filters: Object.values(VatClientType).map(type => ({ text: type, value: type })),
      onFilter: (value: any, record: IVatClient) => record.clientType === value,
      render: (value: VatClientType) => (
        <span style={{ 
          color: value === VatClientType.PURCHASE ? '#52c41a' : '#1890ff',
          fontWeight: 'bold'
        }}>
          {value}
        </span>
      ),
    },
    {
      title: "Наш долг",
      key: "ourDebt",
      render: (record: IVatClient) => (
        <div>
          <div>{formatNumber(record.totalVatDebt.TJS)} TJS</div>
          <small>{formatNumber(record.totalVatDebt.USD)} USD</small>
        </div>
      ),
      sorter: (a: IVatClient, b: IVatClient) => a.totalVatDebt.TJS - b.totalVatDebt.TJS,
    },
    {
      title: "Долг клиента",
      key: "clientDebt",
      render: (record: IVatClient) => (
        <div>
          <div>{formatNumber(record.totalVatCredit.TJS)} TJS</div>
          <small>{formatNumber(record.totalVatCredit.USD)} USD</small>
        </div>
      ),
      sorter: (a: IVatClient, b: IVatClient) => a.totalVatCredit.TJS - b.totalVatCredit.TJS,
    },
    {
      title: "Статус",
      dataIndex: "isActive",
      key: "isActive",
      render: (value: boolean) => (
        <span style={{ color: value ? '#52c41a' : '#ff4d4f' }}>
          {value ? 'Активен' : 'Неактивен'}
        </span>
      ),
      filters: [
        { text: 'Активен', value: true },
        { text: 'Неактивен', value: false },
      ],
      onFilter: (value: any, record: IVatClient) => record.isActive === value,
    },
    {
      title: "Действия",
      key: "actions",
      render: (record: IVatClient) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Редактировать
          </Button>
          <Popconfirm
            title="Вы уверены, что хотите удалить этого клиента?"
            onConfirm={() => handleDelete(record._id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              loading={deleteLoading === record._id}
            >
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ marginBottom: "24px" }}>
        <Row gutter={16}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Наш общий долг"
                value={balance?.ourDebt?.TJS || 0}
                suffix="TJS"
                loading={balanceLoading}
                precision={0}
                valueStyle={{ color: '#ff4d4f' }}
              />
              <small>{formatNumber(balance?.ourDebt?.USD || 0)} USD</small>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Общий долг клиентов"
                value={balance?.clientDebt?.TJS || 0}
                suffix="TJS"
                loading={balanceLoading}
                precision={0}
                valueStyle={{ color: '#52c41a' }}
              />
              <small>{formatNumber(balance?.clientDebt?.USD || 0)} USD</small>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Общий баланс"
                value={totalBalance.TJS}
                suffix="TJS"
                loading={balanceLoading}
                precision={0}
                valueStyle={{ 
                  color: totalBalance.TJS >= 0 ? '#52c41a' : '#ff4d4f',
                  fontWeight: 'bold'
                }}
                prefix={totalBalance.TJS >= 0 ? '+' : ''}
              />
              <small style={{ 
                color: totalBalance.USD >= 0 ? '#52c41a' : '#ff4d4f',
                fontWeight: 'bold'
              }}>
                {totalBalance.USD >= 0 ? '+' : ''}{formatNumber(totalBalance.USD)} USD
              </small>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {totalBalance.TJS >= 0 ? 'Нам должны' : 'Мы должны'}
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      <div style={{ marginBottom: "16px" }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Input
              placeholder="Поиск по названию, ИНН или телефону"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ maxWidth: "400px" }}
            />
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Добавить клиента
            </Button>
          </Col>
        </Row>
      </div>

      <Table
        columns={columns}
        dataSource={clients}
        rowKey="_id"
        loading={loading}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} из ${total} клиентов`,
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title={editingClient ? "Редактировать клиента НДС" : "Добавить клиента НДС"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Название"
                rules={[{ required: true, message: "Введите название" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Телефон"
                rules={[{ required: true, message: "Введите телефон" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="tin"
                label="ИНН"
                rules={[{ required: true, message: "Введите ИНН" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="clientType"
                label="Тип клиента"
                rules={[{ required: true, message: "Выберите тип клиента" }]}
              >
                <Select>
                  {Object.values(VatClientType).map(type => (
                    <Option key={type} value={type}>{type}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="Адрес"
            rules={[{ required: true, message: "Введите адрес" }]}
          >
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name={["bankDetails", "bankName"]}
                label="Название банка"
                rules={[{ required: true, message: "Введите название банка" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={["bankDetails", "accountNumber"]}
                label="Номер счета"
                rules={[{ required: true, message: "Введите номер счета" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={["bankDetails", "mfo"]}
                label="МФО"
                rules={[{ required: true, message: "Введите МФО" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="isActive"
            label="Активен"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Примечания"
          >
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                {editingClient ? "Обновить" : "Создать"}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VatClientsPage;